import React, { useEffect, useState } from "react";
import { getOnesubscription } from "../services/transactionServices";
import { useSelector } from "react-redux";
import {
  X, Receipt, Hash, CreditCard, Package, Sparkles, Copy, Check,
  Info,
} from "lucide-react";
import { useTranslation } from "react-i18next";

/* ================================================================
 *  InfoRow — ردیف اطلاعات با قابلیت کپی
 * ================================================================ */
const InfoRow = ({ icon: Icon, label, value, copyable = false, mono = false }) => {
  const { t, i18n } = useTranslation(["user"]);
  const isEnglish = i18n.language === "en";
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!copyable || !value || value === "---") return;
    try {
      await navigator.clipboard.writeText(String(value));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <div className="flex items-center justify-between gap-3 px-4 py-3">
      <span className="flex items-center gap-2 text-[11px] sm:text-xs font-bold text-slate-500 dark:!text-slate-400">
        <Icon size={13} className="text-teal-600/80 dark:!text-teal-300/80 shrink-0" />
        {label}
      </span>

      <div className="flex items-center gap-2 min-w-0">
        <span
          className={`
            text-xs sm:text-sm font-extrabold text-slate-800 dark:!text-amber-100 tabular-nums truncate max-w-[200px]
            ${mono ? "font-mono" : ""} ${isEnglish && 'font-sans'}
          `}
          dir={mono ? "ltr" : "rtl"}
        >
          {value || "---"}
        </span>
        {copyable && value && value !== "---" && (
          <button
            type="button"
            onClick={handleCopy}
            aria-label={t("transactionInfoModal.copy")}
            className="shrink-0 h-6 w-6 flex items-center justify-center rounded-md text-slate-400 hover:text-teal-600 dark:!hover:text-teal-300 hover:bg-teal-500/10 transition-colors"
          >
            {copied ? (
              <Check size={12} className="text-emerald-500" />
            ) : (
              <Copy size={12} />
            )}
          </button>
        )}
      </div>
    </div>
  );
};

/* ================================================================
 *  Main
 * ================================================================ */
export default function TransactionInfoModal({ isOpen, closeModal, modalData }) {
  const { t, i18n } = useTranslation(["user"]);
  const isEnglish = i18n.language === "en";

  const [planData, setPlanData] = useState([]);
  const accessToken = useSelector((state) => state.auth.accessToken);

  useEffect(() => {
    if (!isOpen || !modalData?.subId) return;

    const subInfo = async () => {
      try {
        const res = await getOnesubscription({
          accessToken,
          id: modalData.subId,
        });
        if (res?.ok) setPlanData(res.data);
      } catch (error) {
        console.log(error);
      }
    };
    subInfo();
  }, [accessToken, isOpen, modalData?.subId]);

  /* ---------- Escape to close ---------- */
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") closeModal();
    };
    if (isOpen) document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [isOpen, closeModal]);

  if (!modalData) return null;

  /* ---------- Status meta ---------- */
  const statusKey =
    modalData?.status === "success"
      ? "success"
      : modalData?.status === "fail"
        ? "fail"
        : modalData?.status === "pending"
          ? "pending"
          : "cancel";

  const statusLabel = t(`transactionInfoModal.status.${statusKey}`);

  const statusTone =
    modalData?.status === "success"
      ? "from-emerald-500/20 to-teal-500/10 border-emerald-400/40 text-emerald-700 dark:!text-emerald-300"
      : modalData?.status === "fail"
        ? "from-rose-500/20 to-rose-400/10 border-rose-400/40 text-rose-700 dark:!text-rose-300"
        : modalData?.status === "pending"
          ? "from-amber-500/20 to-amber-400/10 border-amber-400/40 text-amber-800 dark:!text-amber-200"
          : "from-slate-500/20 to-slate-400/10 border-slate-400/40 text-slate-700 dark:!text-slate-300";

  return (
    <div
      className={`
        fixed inset-0 z-[80] flex items-center justify-center px-4
        bg-slate-900/50 dark:!bg-black/70 backdrop-blur-sm
        transition-opacity duration-200
        ${isOpen ? "opacity-100 visible" : "opacity-0 invisible pointer-events-none"}
      `}
      onClick={closeModal}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="
          w-full max-w-md
          rounded-2xl overflow-hidden
          bg-white dark:!bg-slate-900
          border border-teal-500/25 dark:!border-teal-400/20
          shadow-[0_25px_60px_-20px_rgba(15,118,110,0.6)]
          dark:!shadow-[0_25px_60px_-20px_rgba(0,0,0,0.9)]
          animate-[fadeIn_0.2s_ease-out]
        "
      >
        {/* خط طلایی بالا */}
        <div className="h-[2px] bg-gradient-to-l from-transparent via-amber-400/80 to-transparent" />

        {/* ================= Header ================= */}
        <div className="relative px-4 pt-4 pb-3 border-b border-teal-500/10 dark:!border-teal-400/10">
          {/* گلوی طلایی */}
          <div className="pointer-events-none absolute -top-16 -right-16 h-32 w-32 rounded-full bg-amber-300/15 blur-3xl dark:!bg-amber-400/10" />

          <div className="relative flex items-start justify-between gap-3">
            <div className="flex items-center gap-2.5 min-w-0">
              <span className="shrink-0 flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-amber-300/40 to-teal-400/20 border border-amber-300/40">
                <Receipt size={17} className="text-amber-500" />
              </span>
              <div className="min-w-0">
                <h3 className="text-sm font-bold text-slate-800 dark:!text-amber-100 truncate">
                  {t("transactionInfoModal.title")}
                </h3>
                <p className="text-[11px] text-slate-500 dark:!text-slate-400 mt-0.5 truncate">
                  {t("transactionInfoModal.botLabel")}{" "}
                  <span className="font-bold text-teal-700 dark:!text-teal-300">
                    {modalData?.business?.username || "---"}
                  </span>
                </p>
              </div>
            </div>

            <button
              onClick={closeModal}
              aria-label={t("transactionInfoModal.close")}
              className="shrink-0 h-8 w-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-rose-500 hover:bg-rose-500/10 transition-colors"
            >
              <X size={16} />
            </button>
          </div>

          {/* Status chip */}
          <div className="relative mt-3">
            <span
              className={`
                inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg
                text-[11px] font-extrabold
                bg-gradient-to-l border
                ${statusTone}
              `}
            >
              <span
                className={`
                  h-1.5 w-1.5 rounded-full
                  ${modalData?.status === "success"
                    ? "bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.9)]"
                    : modalData?.status === "fail"
                      ? "bg-rose-500 shadow-[0_0_6px_rgba(244,63,94,0.9)]"
                      : modalData?.status === "pending"
                        ? "bg-amber-500 shadow-[0_0_6px_rgba(251,191,36,0.9)]"
                        : "bg-slate-500"
                  }
                `}
              />
              {statusLabel}
            </span>
          </div>
        </div>

        {/* ================= Body ================= */}
        <div className="divide-y divide-teal-500/10 dark:!divide-teal-400/10 max-h-[60vh] overflow-y-auto custom-scrollbar">

          <InfoRow
            icon={Hash}
            label={t("transactionInfoModal.fields.transactionCode")}
            value={modalData?.id}
            copyable
          />

          {modalData?.refId && (
            <InfoRow
              icon={Hash}
              label={t("transactionInfoModal.fields.trackingCode")}
              value={modalData?.refId}
              copyable
            />
          )}

          {modalData?.cardNumber && (
            <InfoRow
              icon={CreditCard}
              label={t("transactionInfoModal.fields.cardNumber")}
              value={modalData?.cardNumber}
              copyable
              mono
            />
          )}

          {planData?.plan?.name && (
            <InfoRow
              icon={Package}
              label={t("transactionInfoModal.fields.planName")}
              value={planData?.plan?.name}
            />
          )}

          {modalData?.paidAmount && (
            <InfoRow
              icon={Sparkles}
              label={t("transactionInfoModal.fields.paidAmount")}
              value={`${Number(modalData.paidAmount).toLocaleString(isEnglish ? "en-US" : "fa-IR")} ${t("transactionInfoModal.currency")}`}
            />
          )}
        </div>

        {/* ================= Footer ================= */}
        <div className="px-4 py-3 border-t border-teal-500/10 dark:!border-teal-400/10 bg-teal-500/5 dark:!bg-teal-400/5 flex items-center justify-between gap-3">
          <span className="flex items-center gap-1.5 text-[10px] text-slate-500 dark:!text-slate-400">
            <Info size={11} className="text-teal-600/80 dark:!text-teal-300/80" />
            {t("transactionInfoModal.infoHint")}
          </span>

          <button
            onClick={closeModal}
            className="
              flex items-center justify-center gap-1.5
              px-4 py-2 rounded-xl
              text-xs font-bold
              text-slate-700 dark:!text-slate-200
              bg-white/60 dark:!bg-white/5
              border border-teal-500/20 dark:!border-teal-400/20
              hover:border-amber-300/60 hover:bg-amber-300/10
              transition-all duration-200
              hover:scale-[1.03] active:scale-95
            "
          >
            {t("transactionInfoModal.close")}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.95) translateY(8px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: rgba(20, 184, 166, 0.55) transparent;
        }
        .custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          border-radius: 999px;
          background-image: linear-gradient(180deg, rgba(20,184,166,0.55), rgba(251,191,36,0.55));
        }
      `}</style>
    </div>
  );
}